import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || ''

/**
 * Errors from the Spotlight API, carrying the HTTP status so callers can tell
 * "you are not signed in" (401) from "you are signed in but not allowed" (403)
 * from "something broke" (500). The rest of this codebase swallows all three
 * into console.error, which is why the old page rendered an empty grid on a
 * permission failure.
 */
export class SpotlightError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'SpotlightError'
    this.status = status ?? null
  }
}

export const isUnauthorized = (error) => error instanceof SpotlightError && error.status === 401
export const isForbidden = (error) => error instanceof SpotlightError && error.status === 403

const request = async (config) => {
  let response

  try {
    response = await axios({
      withCredentials: true,
      timeout: 20000,
      ...config,
      url: `${API_URL}${config.url}`,
    })
  } catch (error) {
    if (axios.isCancel(error)) throw error

    const status = error.response?.status ?? null

    if (status === 401) throw new SpotlightError('Your session has expired.', 401)
    if (status === 403) throw new SpotlightError('Spotlight is available to admins only.', 403)
    if (status === 429) {
      throw new SpotlightError('Too many requests — give it a moment and try again.', 429)
    }

    // 4xx bodies come from our own controllers, which write their messages to be
    // shown ("A valid \"org\" parameter is required"). 5xx must NOT be echoed:
    // requests that fail inside middleware bypass the controllers' generic
    // strings and can surface a raw fault message.
    const serverMessage =
      typeof error.response?.data?.error === 'string' ? error.response.data.error : null
    const showServerMessage = serverMessage && status !== null && status >= 400 && status < 500

    throw new SpotlightError(
      showServerMessage
        ? serverMessage
        : 'Could not reach the server. Check your connection and try again.',
      status
    )
  }

  // A proxy error page, an HTML login redirect or a gateway timeout can all
  // arrive as a 200 carrying something that is not our JSON envelope. Without
  // this the failure surfaces later as "Cannot read properties of undefined"
  // from deep inside a component.
  const data = response.data
  if (data === null || typeof data !== 'object') {
    throw new SpotlightError('The server returned an unexpected response.', response.status)
  }

  return data
}

/**
 * Turn the filter state into query params.
 * Empty values are omitted entirely so the URL stays readable and the server
 * sees only what is actually being filtered on.
 */
const buildParams = (filters = {}, extra = {}) => {
  const params = {}

  const addList = (key, values) => {
    if (Array.isArray(values) && values.length) params[key] = values.join(',')
  }

  if (filters.search) params.search = filters.search
  addList('domain_keys', filters.domainKeys)
  // The server accepts subdomain filtering, but the rail exposes the ~60 domains
  // rather than the ~150 subdomains, so nothing sets this today. Kept because
  // addList no-ops on an absent key and this is the one place the query shape is
  // defined — surfacing that facet later needs no change here.
  addList('subdomain_keys', filters.subdomainKeys)
  addList('country_codes', filters.countryCodes)
  addList('cities', filters.cities)
  addList('role_family_keys', filters.roleFamilyKeys)
  addList('content_types', filters.contentTypes)
  addList('size_tiers', filters.sizeTiers)
  // Organisation type. Absent means the server's default (companies only).
  addList('kinds', filters.orgKinds)

  if (filters.remote) params.remote = 'true'
  if (filters.hiring) params.hiring = 'true'
  if (filters.savedOnly) params.saved = 'true'
  // One booth per employer by default; place_id is a Maps location, so Saab owns
  // 13 rows and Sweco 15.
  if (filters.allLocations) params.all_locations = 'true'
  if (filters.sort) params.sort = filters.sort

  return { ...params, ...extra }
}

export const fetchCompanies = ({ filters, limit, offset, signal } = {}) =>
  request({
    method: 'get',
    url: '/spotlight/companies',
    params: buildParams(filters, { limit, offset }),
    signal,
  })

export const fetchCompany = ({ orgKey, signal }) =>
  request({
    method: 'get',
    url: '/spotlight/company',
    params: { org: orgKey },
    signal,
  })

export const fetchFilterOptions = ({ signal } = {}) =>
  request({ method: 'get', url: '/spotlight/filter-options', signal })

export const fetchStats = ({ signal } = {}) =>
  request({ method: 'get', url: '/spotlight/stats', signal })

export const setCompanySaved = ({ orgKey, saved }) =>
  request({
    method: 'put',
    url: '/spotlight/saved',
    data: { orgKey, saved },
    headers: { 'Content-Type': 'application/json' },
  })

/** Register for, or withdraw from, a hiring sprint. Idempotent. */
export const setSprintRegistration = ({ sprintId, registered, note }) =>
  request({
    method: 'put',
    url: '/spotlight/sprint-registration',
    data: { sprintId, registered, note },
    headers: { 'Content-Type': 'application/json' },
  })

/**
 * Ask the company something. It is sent to them privately and only appears on
 * the booth once they answer — this is not a public comment.
 */
export const askQuestion = ({ orgKey, sprintId, question }) =>
  request({
    method: 'post',
    url: '/spotlight/question',
    data: { orgKey, sprintId, question },
    headers: { 'Content-Type': 'application/json' },
  })

export { buildParams }
